import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useScopedPath } from "@/lib/scoped";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, CreditCard } from "lucide-react";

function formatPounds(pence) {
  if (!pence) return "£0.00";
  return `£${(pence / 100).toFixed(2)}`;
}

/**
 * League-scoped paywall (iter142 protocol shift).
 *
 * Replaces `MatchDayPaymentGate` — instead of one payment per match-day,
 * a single "Current league entry" payment covers every match in the
 * active season. Once paid the user sees the fixture list unlocked
 * end-to-end. When Stripe is disabled for the pub, this returns null so
 * every match is free.
 *
 * Parent hook: `onAccessChange(hasAccess)` — MatchesPage uses this to
 * hide/show the prediction forms in one place rather than per-card.
 */
export default function LeagueEntryGate({ onAccessChange }) {
  const scope = useScopedPath();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payments/league/access");
      setInfo(data);
      onAccessChange?.(!data?.stripe_enabled || Boolean(data?.has_access));
    } catch (e) {
      // Fail-open so free-play environments never accidentally lock — the
      // backend still enforces on the predictions submit path.
      onAccessChange?.(true);
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [onAccessChange]);

  useEffect(() => { load(); }, [load]);

  const startCheckout = async () => {
    setBusy(true);
    try {
      // Tenant-scoped origin so the success page lands inside /c/{slug}/…
      const scopedRoot = scope("/").replace(/\/$/, "");
      const originUrl = `${window.location.origin}${scopedRoot}`;
      const { data } = await api.post("/payments/league/checkout", {
        origin_url: originUrl,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  if (loading || !info) return null;
  if (!info.stripe_enabled) return null;   // Free play — no gate
  if (info.has_access) return null;        // Already paid this season

  const fee = info.league_fee_pence;

  return (
    <section
      data-testid="league-entry-gate"
      className="mb-6 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 sm:px-5 sm:py-3"
    >
      <div className="flex items-center gap-4 flex-wrap sm:flex-nowrap">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-sm bg-amber-900 text-white shrink-0">
          <Lock className="h-4 w-4" />
        </span>
        <p
          className="flex-1 min-w-[220px] text-sm text-amber-900 leading-snug"
          data-testid="league-gate-headline"
        >
          <strong className="text-amber-950">Current Gameweek entry {formatPounds(fee)}.</strong>{" "}
          One payment unlocks every match in the current Gameweek. Payments are processed by
          Stripe — you&apos;ll get an emailed receipt.
        </p>
        <Button
          type="button"
          onClick={startCheckout}
          disabled={busy}
          data-testid="pay-league-entry-btn"
          className="h-10 px-4 rounded-sm bg-[#0038FF] hover:bg-[#002BE6] text-white font-semibold inline-flex items-center gap-2 shrink-0 sm:ml-auto"
        >
          <CreditCard className="h-4 w-4" />
          {busy ? "Redirecting…" : `Pay ${formatPounds(fee)}`}
        </Button>
      </div>
    </section>
  );
}
